// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Click-to-define glossary. Any element with class `glossary-link` and a
 * `data-term` attribute becomes a clickable definition trigger. A single
 * floating tooltip is shown; Escape or an outside click dismisses it.
 */

interface GlossaryTerm {
  title: string;
  body: string;
}

export const GLOSSARY: Record<string, GlossaryTerm> = {
  'web-worker': {
    title: 'Web Worker',
    body: 'A background thread in your browser. zipsafe runs all compression and decompression here so the page stays responsive on large files.',
  },
  deflate: {
    title: 'DEFLATE',
    body: 'The lossless compression algorithm used inside ZIP files. It finds and removes repetition so the archive is smaller than the original files.',
  },
  'central-directory': {
    title: 'Central directory',
    body: "A table at the end of every ZIP listing each file's name, size, and location. zipsafe reads it to show the contents without decompressing anything.",
  },
  'file-system-access': {
    title: 'File System Access API',
    body: 'A modern browser capability that lets a web page write files into a folder you explicitly pick — used here to extract straight to disk. Files still never leave your device.',
  },
  'zip-slip': {
    title: 'Zip-Slip',
    body: 'An attack where a malicious archive uses paths like ../../ to write files outside the target folder. zipsafe strips such traversal from every entry before writing.',
  },
};

let tooltip: HTMLElement | null = null;

function ensureTooltip(): HTMLElement {
  if (!tooltip) {
    tooltip = document.createElement('div');
    tooltip.className = 'glossary-tooltip';
    tooltip.setAttribute('role', 'tooltip');
    document.body.appendChild(tooltip);
  }
  return tooltip;
}

function hide(): void {
  if (tooltip) tooltip.classList.remove('show');
}

function show(target: HTMLElement, term: string): void {
  const def = GLOSSARY[term];
  if (!def) return;
  const tip = ensureTooltip();
  tip.innerHTML = `<strong>${def.title}</strong><span>${def.body}</span>`;
  tip.classList.add('show');

  const rect = target.getBoundingClientRect();
  const tipRect = tip.getBoundingClientRect();
  let left = rect.left + window.scrollX;
  const top = rect.bottom + window.scrollY + 8;
  // Keep it on-screen.
  const maxLeft = window.scrollX + document.documentElement.clientWidth - tipRect.width - 12;
  if (left > maxLeft) left = Math.max(window.scrollX + 12, maxLeft);
  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;
}

/** Wire global glossary handling. Call once at startup. */
export function initGlossary(): void {
  document.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    const link = target.closest<HTMLElement>('.glossary-link');
    if (link && link.dataset.term) {
      ev.preventDefault();
      ev.stopPropagation();
      show(link, link.dataset.term);
      return;
    }
    if (tooltip && !target.closest('.glossary-tooltip')) hide();
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') hide();
  });
  window.addEventListener('resize', hide);
  window.addEventListener('scroll', hide, true);
}
